import { getCountryCallingCode, isValidPhoneNumber } from 'react-phone-number-input/max'
import type { Country } from 'react-phone-number-input/max'

/**
 * Returns true when the phone input's display text shows an international
 * calling code the user manually typed, meaning the PhoneInput component
 * should be remounted to reset its internal state to clean national format.
 *
 * Two cases that require a remount:
 *   1. User typed a "+" prefix (e.g. "+44 207...") — unambiguous international entry.
 *   2. User typed the calling code digits without "+" (e.g. "44 207..." with GB selected,
 *      or "1 347..." with US selected) — detected by checking the display starts with
 *      the selected country's calling code followed by a space.
 *
 * Legitimate national formats are never remounted regardless of country:
 *   - Trunk-zero countries (UK "020 7123 4567", DE "030...", FR "01...") start with 0
 *     and cannot match a calling code (E.164 calling codes never start with 0).
 *   - Non-trunk countries (ES "612 34 56 78", IT "06 1234...") start with non-zero digits
 *     but do not match the selected country's own calling code, so they are correctly excluded.
 *
 * Using the actual calling code from phone metadata means this works for every country
 * the app expands to without any code changes.
 */
export function phoneDisplayHasPrefix(
	displayText: string,
	sanitized: string,
	selectedCountry: Country,
): boolean {
	if (displayText.startsWith('+')) return true
	if (!isValidPhoneNumber(sanitized)) return false

	// Only digits-then-space patterns can be a manually typed calling code.
	// Calling codes never start with 0 (ITU-T E.164 reserves 0 as trunk prefix),
	// so /^[1-9]\d*\s/ is a cheap pre-filter before the metadata lookup.
	if (!/^[1-9]\d*\s/.test(displayText)) return false

	// Final check: the leading digits must match this country's calling code.
	// e.g. GB selected (CC = "44"): "44 207..." → remount; "612 34..." → no remount.
	const callingCode = getCountryCallingCode(selectedCountry)
	return displayText.startsWith(`${callingCode} `)
}
